import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../Components/Sidebar/Sidebar";
import Login from "../../Components/Login/Login";
import CommentSection from "../../Components/CommentSection/CommentSection";
import VoteSection from "../../Components/VoteSection/VoteSection";
import { get } from "../../utils/request";
import { t, noAutoTranslateUserTextProps, mergeReaderNameClass } from "../../utils/i18n";
import {
  normalizeModeratorsFromArticleData,
  createTopicModeratorChecker,
} from "../../utils/topicModerators";
import ModeratorVerifiedBadge from "../../Components/ModeratorVerifiedBadge/ModeratorVerifiedBadge";
import styles from "./PostDetail.module.css";

const PostDetail = ({
  theme,
  setTheme,
  isLoggedIn,
  handleOpenLogin,
  cartDisplayed,
  handleCloseCart,
  handleLoginSuccess,
}) => {
  const { postId } = useParams();
  const navigate = useNavigate();

  const [postDetail, setPostDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [postTopicModerators, setPostTopicModerators] = useState([]);

  const formatDateTime = value => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  };

  const postTopicModChecker = useMemo(
    () => createTopicModeratorChecker(postTopicModerators),
    [postTopicModerators],
  );

  useEffect(() => {
    if (!postId) return;
    let cancelled = false;
    const fetchPost = async () => {
      setLoading(true);
      try {
        const res = await get(`/social/post/detail/${postId}`);
        const data = res?.data || {};
        if (!cancelled) {
          const vid = data.voteId ?? data.voteID ?? data.vetoId;
          const articleId =
            data.articleId ??
            data.articleID ??
            data.topicId ??
            data.socialArticleId ??
            (data.article && data.article.id) ??
            "";
          setPostDetail({
            id: data.id ?? postId,
            title: data.title || "",
            content: data.content || "",
            username: data.username || "",
            userId: data.userId ?? data.userID ?? data.createUserId ?? "",
            createTime: data.createTime || "",
            likeCnt: Number(data.likeCnt || 0),
            commentCnt: Number(data.commentCnt || 0),
            articleId: articleId != null && articleId !== "" ? String(articleId) : "",
            voteId:
              vid != null && String(vid).trim() !== "" ? vid : null,
          });
        }
      } catch (e) {
        if (!cancelled) {
          setPostDetail(null);
          setPostTopicModerators([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPost();
    return () => {
      cancelled = true;
    };
  }, [postId]);

  useEffect(() => {
    const aid = postDetail?.articleId;
    if (!aid) {
      setPostTopicModerators([]);
      return;
    }
    let cancelled = false;
    get(`/social/article/${aid}`)
      .then(res => {
        if (cancelled) return;
        const data = res?.data || {};
        setPostTopicModerators(normalizeModeratorsFromArticleData(data));
      })
      .catch(() => {
        if (!cancelled) setPostTopicModerators([]);
      });
    return () => {
      cancelled = true;
    };
  }, [postDetail?.articleId]);

  useEffect(() => {
    if (loading || !postDetail) return;
    if (window.location.hash !== "#post-comments") return;
    const id = requestAnimationFrame(() => {
      document
        .getElementById("post-comments")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(id);
  }, [loading, postDetail]);

  return (
    <div className={styles.page} data-theme={theme}>
      {cartDisplayed ? (
        <Login
          cartDisplayed={cartDisplayed}
          handleCloseCart={handleCloseCart}
          onLoginSuccess={handleLoginSuccess}
        />
      ) : null}
      <div className={styles.container}>
        <Sidebar
          isOpen={false}
          onClose={() => {}}
          theme={theme}
          setTheme={setTheme}
          isLoggedIn={isLoggedIn}
          handleOpenLogin={handleOpenLogin}
        />
        <main className={styles.mainContent}>
          <header className={styles.headerBar}>
            <button
              type="button"
              className={styles.backBtn}
              onClick={() => navigate(-1)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span>{t("postDetail.back", "Back")}</span>
            </button>
          </header>
          <div className={styles.scrollArea}>
            <article className={styles.card}>
              {loading && (
                <div className={styles.placeholder}>
                  {t("postDetail.loading", "loading...")}
                </div>
              )}
              {!loading && !postDetail && (
                <div className={styles.placeholder}>
                  {t("postDetail.notFound", "Post not found or deleted")}
                </div>
              )}
              {!loading && postDetail && (
                <>
                  <h1 className={styles.title}>
                    {postDetail.title ? (
                      <span
                        className="notranslate"
                        {...noAutoTranslateUserTextProps}
                      >
                        {postDetail.title}
                      </span>
                    ) : (
                      t("postDetail.untitled", "Untitled Post")
                    )}
                  </h1>
                  <div className={styles.metaRow}>
                    <span className={styles.metaAuthor}>
                      {postDetail.username ? (
                        <>
                          <span
                            className={mergeReaderNameClass(true)}
                            {...noAutoTranslateUserTextProps}
                          >
                            {postDetail.username}
                          </span>
                          {postTopicModChecker(
                            postDetail.userId,
                            postDetail.username,
                            postDetail,
                          ) ? (
                            <ModeratorVerifiedBadge />
                          ) : null}
                        </>
                      ) : (
                        t("postDetail.unknownUser", "Unknown User")
                      )}
                    </span>
                    <span>·</span>
                    <span>
                      {formatDateTime(postDetail.createTime) ||
                        t("postDetail.unknownTime", "Unknown Time")}
                    </span>
                    <span>·</span>
                    <span>
                      {t("postDetail.likes", "{count} likes", {
                        count: Number(postDetail.likeCnt || 0),
                      })}
                    </span>
                    <span>·</span>
                    <span>
                      {t("postDetail.comments", "{count} comments", {
                        count: Number(postDetail.commentCnt || 0),
                      })}
                    </span>
                  </div>
                  <div
                    className={`${styles.content} notranslate`}
                    {...noAutoTranslateUserTextProps}
                    dangerouslySetInnerHTML={{
                      __html:
                        postDetail.content ||
                        `<p>${t("postDetail.noDescription", "No description")}</p>`,
                    }}
                  />
                </>
              )}
            </article>

            {postDetail?.voteId != null &&
            String(postDetail.voteId).trim() !== "" ? (
              <section
                className={styles.voteCard}
                aria-label={t("postDetail.aria.poll", "Post poll")}
              >
                <VoteSection
                  voteId={postDetail.voteId}
                  isLoggedIn={isLoggedIn}
                  onRequireLogin={handleOpenLogin}
                />
              </section>
            ) : null}

            <section
              className={styles.commentCard}
              id="post-comments"
            >
              <CommentSection
                category="POST"
                objectId={postId}
                isLoggedIn={isLoggedIn}
                onRequireLogin={handleOpenLogin}
                hideTabs={false}
                totalFromDetail={Number(postDetail?.commentCnt || 0)}
                topicModerators={postTopicModerators}
              />
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PostDetail;
